import React, { useRef, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { clsx } from 'clsx'
import { Upload, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react'
import ImageCropModal from './ImageCropModal'

interface ImageUploadProps {
  currentImageUrl?: string | null
  onUpload: (file: File) => Promise<void>
  onRemove?: () => Promise<void>
  accept?: string
  maxSizeMB?: number
  label?: string
  helpText?: string
  aspectRatio?: 'square' | 'landscape' | 'auto'
  disabled?: boolean
  isUploading?: boolean
  /** Show deleting state instead of uploading state */
  isDeleting?: boolean
  /** Enable image cropping before upload. When true, opens a crop modal after file selection. */
  enableCrop?: boolean
  /** Aspect ratio for cropping (only used when enableCrop is true). Default: 1 (square) */
  cropAspectRatio?: number
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImageUrl,
  onUpload,
  onRemove,
  accept = 'image/png,image/jpeg,image/webp',
  maxSizeMB = 2,
  label,
  helpText,
  aspectRatio = 'auto',
  disabled = false,
  isUploading = false,
  isDeleting = false,
  enableCrop = false,
  cropAspectRatio = 1
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null)

  const maxSizeBytes = maxSizeMB * 1024 * 1024

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      const acceptedTypes = accept.split(',').map((t) => t.trim())
      if (!acceptedTypes.includes(file.type)) {
        const friendlyTypes = acceptedTypes
          .map((t) => t.replace('image/', '').toUpperCase())
          .join(', ')
        return `Invalid file type. Accepted: ${friendlyTypes}`
      }

      // Check file size
      if (file.size > maxSizeBytes) {
        return `File size exceeds ${maxSizeMB}MB limit`
      }

      return null
    },
    [accept, maxSizeBytes, maxSizeMB]
  )

  const handleFile = useCallback(
    async (file: File) => {
      setError(null)

      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        return
      }

      // If cropping is enabled, open crop modal instead of uploading directly
      if (enableCrop) {
        const reader = new FileReader()
        reader.onload = (e) => {
          setSelectedImageSrc(e.target?.result as string)
          setCropModalOpen(true)
        }
        reader.readAsDataURL(file)
        return
      }

      // Direct upload without cropping
      // Read file for preview first, then call upload handler
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target?.result as string)
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsDataURL(file)
      })

      setPreviewUrl(dataUrl)

      try {
        await onUpload(file)
        // Keep preview visible - don't clear it
        // The preview will be replaced when currentImageUrl prop changes (e.g., after server upload)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed')
        setPreviewUrl(null)
      }
    },
    [onUpload, validateFile, enableCrop]
  )

  // Handle cropped image from modal
  const handleCroppedImage = useCallback(
    async (croppedFile: File) => {
      setCropModalOpen(false)
      setSelectedImageSrc(null)

      // Create preview of cropped image
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target?.result as string)
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsDataURL(croppedFile)
      })

      setPreviewUrl(dataUrl)

      // Upload the cropped file
      try {
        await onUpload(croppedFile)
        // Keep preview visible - don't clear it
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed')
        setPreviewUrl(null)
      }
    },
    [onUpload]
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFile(file)
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled && !isUploading) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    if (disabled || isUploading) return

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFile(file)
    }
  }

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click()
    }
  }

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onRemove) {
      try {
        await onRemove()
        setPreviewUrl(null)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove image')
      }
    }
  }

  const displayUrl = previewUrl || currentImageUrl

  const aspectRatioClasses = {
    square: 'aspect-square',
    landscape: 'aspect-video',
    auto: ''
  }

  return (
    <div className='space-y-2'>
      {label && (
        <label className='block text-sm font-medium text-gray-700'>
          {label}
        </label>
      )}

      <input
        ref={fileInputRef}
        type='file'
        accept={accept}
        onChange={handleInputChange}
        className='hidden'
        disabled={disabled || isUploading}
      />

      {(isUploading || isDeleting) ? (
        // Loading state
        <div className='flex items-center gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50'>
          <div className='w-16 h-16 flex items-center justify-center rounded-lg border border-gray-200 bg-white'>
            <Loader2 className={`h-8 w-8 animate-spin ${isDeleting ? 'text-red-500' : 'text-avi-primary'}`} />
          </div>
          <div className='flex-1'>
            <p className='text-sm font-medium text-gray-900'>
              {isDeleting ? 'Deleting...' : 'Uploading...'}
            </p>
            <p className='text-xs text-gray-500'>Please wait</p>
          </div>
        </div>
      ) : displayUrl ? (
        // Compact horizontal layout when image exists - with drag and drop support
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={clsx(
            'flex items-center gap-4 p-4 border rounded-lg transition-all duration-200',
            isDragOver
              ? 'border-blue-500 border-2 bg-blue-50'
              : 'border-gray-200 bg-gray-50'
          )}
        >
          <div className='w-24 h-24 rounded-lg border border-gray-200 bg-white flex items-center justify-center'>
            <img
              src={displayUrl}
              alt='Logo preview'
              className='w-20 h-20 object-contain'
              crossOrigin='anonymous'
              onError={() => setError('Failed to load image')}
            />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium text-gray-900'>Current Logo</p>
            <p className='text-xs text-gray-500'>
              {isDragOver ? 'Drop to replace logo' : 'Drag & drop or click "Change"'}
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={handleClick}
              disabled={disabled}
              className='px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Change
            </button>
            {onRemove && (
              <button
                type='button'
                onClick={handleRemove}
                disabled={disabled}
                className='p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <Trash2 className='h-5 w-5' />
              </button>
            )}
          </div>
        </div>
      ) : (
        // Full-width drop zone for initial upload
        <motion.div
          whileHover={!disabled && !isUploading ? { scale: 1.01 } : {}}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={clsx(
            'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed transition-all duration-200 cursor-pointer overflow-hidden',
            aspectRatioClasses[aspectRatio],
            !aspectRatio || aspectRatio === 'auto' ? 'min-h-[160px]' : '',
            isDragOver
              ? 'border-avi-primary bg-avi-primary/5'
              : 'border-gray-300 hover:border-gray-400',
            disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-gray-50',
            error ? 'border-red-300 bg-red-50' : ''
          )}
        >
          <div className='flex flex-col items-center justify-center p-6'>
            <div className='p-3 bg-gray-100 rounded-full'>
              {error ? (
                <ImageIcon className='h-8 w-8 text-red-400' />
              ) : (
                <Upload className='h-8 w-8 text-gray-400' />
              )}
            </div>
            <p className='mt-3 text-sm font-medium text-gray-700'>
              {error ? 'Try again' : 'Click or drag to upload'}
            </p>
            <p className='mt-1 text-xs text-gray-500'>
              {accept
                .split(',')
                .map((t) => t.replace('image/', '').toUpperCase())
                .join(', ')}{' '}
              up to {maxSizeMB}MB
            </p>
          </div>
        </motion.div>
      )}

      {error && (
        <p className='text-sm text-red-600 flex items-center gap-1'>
          <span className='inline-block w-1 h-1 bg-red-600 rounded-full'></span>
          {error}
        </p>
      )}

      {helpText && !error && (
        <p className='text-sm text-gray-500'>{helpText}</p>
      )}

      {/* Crop Modal */}
      {selectedImageSrc && (
        <ImageCropModal
          isOpen={cropModalOpen}
          onClose={() => {
            setCropModalOpen(false)
            setSelectedImageSrc(null)
          }}
          imageSrc={selectedImageSrc}
          onCropComplete={handleCroppedImage}
          aspectRatio={cropAspectRatio}
          title='Crop Logo'
        />
      )}
    </div>
  )
}

export default ImageUpload
